/**
 * Calculator Export/Import Hook
 * Provides functionality to save and restore calculator configurations
 */

import { useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  serializeCalculatorState,
  deserializeCalculatorState,
  validateReversePitiState,
  generateExportFileName,
  downloadAsJson,
  readFileAsText,
  CalculatorExport,
} from '@/utils/calculator/stateSerializer';

export interface UseCalculatorExportOptions {
  calculatorType: CalculatorExport['calculatorType'];
  getState: () => Record<string, any>;
  loadState: (state: Record<string, any>) => void;
}

export interface UseCalculatorExportReturn {
  exportState: (metadata?: { name?: string; description?: string }) => void;
  importState: () => void;
  isImporting: boolean;
}

export function useCalculatorExport(
  options: UseCalculatorExportOptions
): UseCalculatorExportReturn {
  const { calculatorType, getState, loadState } = options;
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Export current state
  const exportState = useCallback(
    (metadata?: { name?: string; description?: string }) => {
      try {
        const state = getState();
        const exportData = serializeCalculatorState(state, calculatorType, metadata);
        const fileName = generateExportFileName(calculatorType, metadata);
        
        downloadAsJson(exportData, fileName);
        
        toast({
          title: 'Configuration exported',
          description: `Saved as ${fileName}`,
        });
      } catch (error) {
        console.error('Export failed:', error);
        toast({
          title: 'Export failed',
          description: 'Could not export calculator configuration',
          variant: 'destructive',
        });
      }
    },
    [calculatorType, getState, toast]
  );

  // Import state from file
  const importState = useCallback(() => {
    // Create hidden file input
    if (!fileInputRef.current) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.style.display = 'none';
      
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;

        try {
          const text = await readFileAsText(file);
          const parseResult = deserializeCalculatorState(text);
          
          // Handle parse failure
          if (parseResult.success === false) {
            toast({
              title: 'Import failed',
              description: parseResult.error,
              variant: 'destructive',
            });
            return;
          }

          const data = parseResult.data;

          // Validate calculator type matches
          if (data.calculatorType !== calculatorType) {
            toast({
              title: 'Wrong calculator type',
              description: `This file is for ${data.calculatorType}, not ${calculatorType}`,
              variant: 'destructive',
            });
            return;
          }

          // Validate state values
          const validation = validateReversePitiState(data.state);
          
          // Handle validation failure
          if (validation.success === false) {
            toast({
              title: 'Invalid configuration',
              description: validation.error,
              variant: 'destructive',
            });
            return;
          }

          // Load the state
          loadState(validation.data);

          toast({
            title: 'Configuration imported',
            description: data.metadata?.name || 'Successfully loaded calculator settings',
          });
        } catch (error) {
          console.error('Import failed:', error);
          toast({
            title: 'Import failed',
            description: 'Could not read the selected file',
            variant: 'destructive',
          });
        }

        // Reset input for next use
        input.value = '';
      };

      document.body.appendChild(input);
      fileInputRef.current = input;
    }

    fileInputRef.current.click();
  }, [calculatorType, loadState, toast]);

  return {
    exportState,
    importState,
    isImporting: false,
  };
}
